// ===== RESPONSES DO BACKEND =====

export interface AssetResponse {
  id: string
  name: string
  s3_key: string
  width: number
  height: number
  bytes: number
  created_at: string
  download_url: string
}

export interface AssetListResponse {
  assets: AssetResponse[]
  total: number
  page: number
  pages: number
}

export interface TemplateResponse {
  id: string
  name: string
  canvas_width: number
  canvas_height: number
  created_at?: string
}

export interface LayerResponse {
  id: string
  template_id: string
  kind: 'asset' | 'text'
  z_index: number
  x: number
  y: number
  w?: number
  h?: number
  rotation: number
  asset_id?: string
  text_content?: string
  style_id?: string
}

export interface TemplateWithLayersResponse extends TemplateResponse {
  layers: LayerResponse[]
}

export interface CanvasPreset {
  name: string
  width: number
  height: number
}

export interface CanvasPresetsResponse {
  presets: CanvasPreset[]
}

export interface TextStyleResponse {
  id: string
  family: string
  size: number
  weight: number
  color_hex: string
  align: string
}

export interface ArtJobResponse {
  id: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  prompt?: string
  seed: number
  model: string
  s3_key?: string
  s3_url?: string
  width?: number
  height?: number
  input_photos?: string[]
  kv_photo?: string
  modo_kv?: boolean
  modo_lucky?: boolean
  zip_s3_key?: string
  zip_s3_url?: string
  original_request?: Record<string, any>  // Armazena a requisição original para refaraz
  error?: string
}

export interface ArtJobListResponse {
  jobs: ArtJobResponse[]
  total: number
  page: number
  pages: number
}

export interface ArtImageResponse {
  id: string
  job_id: string
  s3_key: string
  s3_url?: string
  width: number
  height: number
  index: number
  created_at: string
  download_url?: string
}

export interface ConjuntoResponse {
  id: string
  name: string
  template_id: string
  art_image_id: string
  art_x: number
  art_y: number
  art_w?: number
  art_h?: number
  art_rotation: number
  text_overrides: Record<string, string>
  created_at: string
  s3_url?: string
}

export interface ConjuntoVersionResponse {
  id: string
  conjunto_id: string
  snapshot: any
  created_at: string
}

export interface ExportResponse {
  export_id: string
  download_url: string
  width: number
  height: number
  size_mb: number
}

export interface LoginResponse {
  access_token: string
  token_type: string
}

export interface LoginRequest {
  email: string
  password: string
}

// ===== TIPOS PARA CRIAR/ATUALIZAR =====

export interface CreateAssetRequest {
  file: File
}

export interface CreateTemplateRequest {
  name: string
  canvas_width: number
  canvas_height: number
}

export interface CreateLayerRequest {
  kind: 'asset' | 'text'
  z_index: number
  x: number
  y: number
  w?: number
  h?: number
  rotation?: number
  asset_id?: string
  text_content?: string
  style_id?: string
  skip_normalization?: boolean
}

export interface CreateArtJobRequest {
  prompt?: string
  seed?: number
  input_photos?: string[]
  model?: string
  width?: number
  height?: number
  mode?: 'generate' | 'regenerate'
  image_url?: string
  modo_kv?: boolean
  modo_lucky?: boolean
  selected_variations?: string[]
}

export interface LuckyVariationsResponse {
  variations: string[]
}

export interface CreateConjuntoRequest {
  name: string
  template_id: string
  art_id: string
  art_x?: number
  art_y?: number
  art_w?: number
  art_h?: number
  art_rotation?: number
  text_overrides?: Record<string, string>
}

export interface UpdateConjuntoRequest {
  art_x?: number
  art_y?: number
  art_w?: number
  art_h?: number
  art_rotation?: number
  text_overrides?: Record<string, string>
}

export interface CreateTextStyleRequest {
  family: string
  size: number
  weight: number
  color_hex: string
  align: string
}

// ===== TIPOS FRONTEND (mapeamento backend → frontend) =====

export interface FrontendAsset {
  id: string
  name: string
  imageUrl: string // download_url do backend
  x: number
  y: number
  z: number
  w: number
  h: number
}

export interface FrontendTemplate {
  id: string
  name: string
  canvasWidth: number
  canvasHeight: number
  layers: FrontendLayer[]
}

export interface FrontendLayer {
  id: string
  kind: 'asset' | 'text'
  zIndex: number
  x: number
  y: number
  w?: number
  h?: number
  rotation: number
  assetId?: string
  textContent?: string
  styleId?: string
}

export interface FrontendArt {
  id: string
  imageUrl: string
  width: number
  height: number
  createdAt: Date
  status?: string
}

// ===== FUNÇÕES HELPER PARA CONVERSÃO =====

export function mapAssetResponseToFrontend(asset: AssetResponse): FrontendAsset {
  return {
    id: asset.id,
    name: asset.name,
    imageUrl: asset.download_url,
    x: 0,
    y: 0,
    z: 0,
    w: asset.width,
    h: asset.height,
  }
}

export function mapTemplateResponseToFrontend(
  template: TemplateWithLayersResponse
): FrontendTemplate {
  return {
    id: template.id,
    name: template.name,
    canvasWidth: template.canvas_width,
    canvasHeight: template.canvas_height,
    layers: template.layers.map((layer) => ({
      id: layer.id,
      kind: layer.kind,
      zIndex: layer.z_index,
      x: layer.x,
      y: layer.y,
      w: layer.w,
      h: layer.h,
      rotation: layer.rotation,
      assetId: layer.asset_id,
      textContent: layer.text_content,
      styleId: layer.style_id,
    })),
  }
}

export function mapArtJobResponseToFrontend(job: ArtJobResponse): FrontendArt {
  return {
    id: job.id,
    imageUrl: job.s3_url || '',
    width: job.width || 0,
    height: job.height || 0,
    createdAt: new Date(),
    status: job.status,
  }
}


